import React, { useState, useRef, useEffect } from "react";
import { typesetMath } from "modules/assessment/AssessmentApp";
import { QuestionRenderer } from "modules/questions/QuestionRenderer";

/**
 * Displays the results of the assessment.
 * @param {object} props - The component props.
 * @param {number} props.score - The final score.
 * @param {object} props.answers - The user's answers.
 * @param {Array<object>} props.questions - The list of questions.
 * @param {Function} props.onRetake - Callback to retake the assessment.
 * @param {Function} props.onReturnHome - Callback to return to the home screen.
 */
export const Results = ({ score, answers, questions, onRetake, onReturnHome }) => {
    const [filter, setFilter] = useState("all");
    const resultsRef = useRef(null);

    useEffect(() => {
        typesetMath(resultsRef.current);
    }, [filter, questions]);

    if (score === undefined || !answers || !questions || questions.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-transparent">
                <div className="container mx-auto p-8 text-center rounded-2xl max-w-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                    <h1 className="text-4xl font-bold text-red-600 dark:text-red-400 mb-4">
                        Results Unavailable
                    </h1>
                    <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                        Error: No assessment data found.
                    </p>
                    <button
                        className="px-8 py-3 bg-blue-500 text-white font-bold rounded-full transition-colors duration-200 hover:bg-blue-600"
                        onClick={onReturnHome}
                    >
                        Return Home
                    </button>
                </div>
            </div>
        );
    }

    const isAutoGraded = (type) => ["MULTIPLE_CHOICE", "TRUE_FALSE", "MATCHING", "QUESTION_TABLE"].includes(type);
    const hasUnderReviewQuestions = questions.some(q => !isAutoGraded(q.questionType));

    const filteredQuestions = questions.filter((question) => {
        const userAnswer = answers[question.id] || "No Answer";
        const isAuto = isAutoGraded(question.questionType);
        const isCorrect = isAuto && (userAnswer ===
            question.answerChoices?.find((choice) => choice.isCorrect)?.choiceText);
        
        if (filter === "correct") return isAuto && isCorrect;
        if (filter === "incorrect") return isAuto && !isCorrect;
        if (filter === "review") return !isAuto;
        return true;
    });

    const CheckCircleIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2l4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );

    const CancelIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );

    const VisibilityIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-gray-500 dark:text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
    );

    return (
        <div
            ref={resultsRef}
            className="container mx-auto p-4 md:p-8 w-full bg-transparent min-h-screen"
        >
            <div className="max-w-7xl mx-auto">
                <div className="text-center p-6 mb-8 border-b-2 border-gray-200 dark:border-gray-800">
                    <h1 className="text-4xl font-extrabold text-black dark:text-white mb-2">
                        Assessment Results
                    </h1>
                    <p className="text-lg text-gray-700 dark:text-gray-300">
                        Detailed breakdown of your performance.
                    </p>
                </div>

                <div className="flex flex-col md:flex-row items-center justify-between p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 mb-8 shadow-md">
                    <div className="text-center md:text-left mb-4 md:mb-0">
                        <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                            Your Score:{" "}
                            <span className="text-blue-600 dark:text-blue-400 font-extrabold">
                                {score}
                            </span>{" "}
                            / {questions.length}
                        </p>
                        {hasUnderReviewQuestions && (
                            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 font-medium bg-amber-50 dark:bg-amber-900/30 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
                                ⚠️ Score is partial. Some questions are under review and awaiting actual tutor evaluation.
                            </p>
                        )}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            className={`flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${filter === "all"
                                ? "bg-blue-500 text-white"
                                : "bg-transparent border border-gray-300 dark:border-gray-700 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                            onClick={() => setFilter("all")}
                        >
                            <VisibilityIcon />
                            <span>All</span>
                        </button>
                        <button
                            className={`flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${filter === "correct"
                                ? "bg-green-500 text-white"
                                : "bg-transparent border border-gray-300 dark:border-gray-700 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                            onClick={() => setFilter("correct")}
                        >
                            <CheckCircleIcon />
                            <span>Correct</span>
                        </button>
                        <button
                            className={`flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${filter === "incorrect"
                                ? "bg-red-500 text-white"
                                : "bg-transparent border border-gray-300 dark:border-gray-700 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                            onClick={() => setFilter("incorrect")}
                        >
                            <CancelIcon />
                            <span>Incorrect</span>
                        </button>
                        {hasUnderReviewQuestions && (
                            <button
                                className={`flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${filter === "review"
                                    ? "bg-amber-500 text-white"
                                    : "bg-transparent border border-gray-300 dark:border-gray-700 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"}`}
                                onClick={() => setFilter("review")}
                            >
                                <span className="text-lg">⏳</span>
                                <span>Review</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="space-y-8">
                    {filteredQuestions.map((question) => {
                        const userAnswer = answers[question.id] || "No Answer";
                        const isAuto = isAutoGraded(question.questionType);
                        const isCorrect = isAuto && (userAnswer ===
                            question.answerChoices?.find((choice) => choice.isCorrect)
                                ?.choiceText);
                        const correctAnswer = question.answerChoices?.find((choice) => choice.isCorrect)
                            ?.choiceText || "Not specified";

                        return (
                            <div
                                key={question.id}
                                className={`p-6 rounded-xl transition-all duration-300 border-l-4 ${!isAuto ? "border-amber-500" : isCorrect ? "border-green-500" : "border-red-500"} bg-white dark:bg-gray-900 shadow-md`}
                            >
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Left Column: Question Details */}
                                    <div>
                                        <QuestionRenderer
                                            question={question}
                                            index={questions.indexOf(question)} />
                                    </div>

                                    {/* Right Column: User Answer, Correct Answer, Explanation */}
                                    <div className="flex flex-col justify-start">
                                        <div className="space-y-4">
                                            {/* User Answer */}
                                            <div className="p-4 border border-gray-200 dark:border-gray-800 rounded-lg">
                                                <p className="text-sm font-semibold text-black dark:text-white flex items-center gap-2">
                                                    Your Answer:
                                                    {!isAuto && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-bold">Under Review</span>}
                                                </p>
                                                <p
                                                    className={`mt-1 text-lg font-medium ${!isAuto ? "text-amber-600" : isCorrect ? "text-green-600" : "text-red-600"}`}
                                                >
                                                    {userAnswer}
                                                </p>
                                            </div>

                                            {/* Correct Answer */}
                                            {isAuto ? (
                                                <div className="p-4 border border-gray-200 dark:border-gray-800 rounded-lg">
                                                    <p className="text-sm font-semibold text-black dark:text-white">
                                                        Correct Answer:
                                                    </p>
                                                    <p className="mt-1 text-lg font-medium text-gray-700 dark:text-gray-300">
                                                        {correctAnswer}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="p-4 border border-gray-200 dark:border-gray-800 rounded-lg">
                                                    <p className="text-sm font-semibold text-black dark:text-white">
                                                        Pending Evaluation
                                                    </p>
                                                    <p className="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        This answer requires manual grading by your tutor.
                                                    </p>
                                                </div>
                                            )}

                                            {/* Explanation */}
                                            {question.answerChoices?.find(
                                                (choice) => choice.isCorrect
                                            )?.explanation && (
                                                    <div className="mt-4 p-4 bg-blue-100 dark:bg-blue-900 rounded-lg border-l-4 border-blue-400 dark:border-blue-600">
                                                        <strong className="text-blue-800 dark:text-blue-200">
                                                            Explanation:
                                                        </strong>
                                                        <p className="text-blue-700 dark:text-blue-300 mt-2 text-sm">
                                                            {question.answerChoices.find(
                                                                (choice) => choice.isCorrect
                                                            )?.explanation}
                                                        </p>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex justify-center space-x-4 mt-8">
                    <button
                        className="px-6 py-2 bg-transparent border border-gray-300 dark:border-gray-700 text-black dark:text-white font-bold rounded-full transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={onReturnHome}
                    >
                        Exit
                    </button>
                </div>
            </div>
        </div>
    );
};
